// Minimal contract for the three monthly subscription plans. Product data
// (prices, benefits, tooltips, images) now lives in the heru-sales-transaction-engine
// catalog (useCatalog) — the single source of truth. What stays here is just
// the stable identity each plan needs for entitlement and module-level config:
// its slug, marketing family and display name. Pair with `useIsPlanActive` /
// `useActiveMonthlyPlan` to detect ownership without re-implementing the filter.

export enum PLAN_SLUG {
	PLATAFORMAS = 'plan_platform',
	PROFESIONISTA = 'plan_entrepreneur',
	EMPRESARIAL = 'plan_business',
}

export type PlanSlug = `${PLAN_SLUG}`;

export type PlanFamily = 'plataformas' | 'profesionista' | 'empresarial';

export type PlanCatalogEntry = {
	slug: PLAN_SLUG;
	family: PlanFamily;
	/** Fallback name for module-level config + while the catalog loads. */
	displayName: string;
};

export const PLAN_CATALOG: Record<PLAN_SLUG, PlanCatalogEntry> = {
	[PLAN_SLUG.PLATAFORMAS]: {
		slug: PLAN_SLUG.PLATAFORMAS,
		family: 'plataformas',
		displayName: 'Plan Plataformas Digitales',
	},
	[PLAN_SLUG.PROFESIONISTA]: {
		slug: PLAN_SLUG.PROFESIONISTA,
		family: 'profesionista',
		displayName: 'Plan RESICO',
	},
	[PLAN_SLUG.EMPRESARIAL]: {
		slug: PLAN_SLUG.EMPRESARIAL,
		family: 'empresarial',
		displayName: 'Actividad Empresarial Plus',
	},
};

// Family → slug, the inverse of PLAN_CATALOG's slug → family. Lets callers that
// decide a plan by family (recommendPlanFromRegimes) resolve the entitlement slug
// without hardcoding it. Exhaustive Record so a new family fails the compile.
const SLUG_BY_FAMILY: Record<PlanFamily, PLAN_SLUG> = {
	plataformas: PLAN_SLUG.PLATAFORMAS,
	profesionista: PLAN_SLUG.PROFESIONISTA,
	empresarial: PLAN_SLUG.EMPRESARIAL,
};

export const planSlugForFamily = (family: PlanFamily): PLAN_SLUG =>
	SLUG_BY_FAMILY[family];

export const PLAN_SLUGS: readonly PLAN_SLUG[] = Object.values(PLAN_SLUG);

export const isPlanSlug = (value: unknown): value is PLAN_SLUG =>
	typeof value === 'string' && PLAN_SLUGS.includes(value as PLAN_SLUG);

export const getPlanBySlug = (slug: string | undefined | null) => {
	if (!slug || !isPlanSlug(slug)) return undefined;
	return PLAN_CATALOG[slug];
};
